// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { defaultLineOptions, chartColor, chartColorAlpha } from '../loadChart';

// ── Group transactions into weekly buckets ─────────────────────────
function buildWeeklyData(transactions) {
  if (!transactions?.length) return null;

  const buckets = {};
  transactions.forEach((t) => {
    if (t.transaction_type === 'INCOME') return; // expenses only
    const week = `Week ${Math.ceil(new Date(t.date).getDate() / 7)}`;
    buckets[week] = (buckets[week] ?? 0) + Math.abs(parseFloat(t.amount) || 0);
  });

  const labels = Object.keys(buckets).sort();
  return { labels, values: labels.map((l) => buckets[l]) };
}

const SAMPLE = {
  labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
  values: [450, 320, 580, 420],
};

export default function CurrentMonthChart({ data = null }) {
  const { labels, values } = useMemo(
    () => buildWeeklyData(data) ?? SAMPLE,
    [data],
  );

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Weekly Expenses',
        data: values,
        backgroundColor: chartColorAlpha(0, 0.1),
        borderColor: chartColor(0),
        borderWidth: 3,
        tension: 0.4,
        fill: true,
        pointBackgroundColor: chartColor(0),
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 5,
        pointHoverRadius: 7,
      },
    ],
  };

  return (
    <div className="h-64 w-full">
      <Line
        data={chartData}
        options={defaultLineOptions('Monthly Expense Trends')}
      />
    </div>
  );
}
